/**
 * Power-ups you wear or wield (spec 02 §7.5): the sneaker, the magic staffs, the wrapped candies,
 * the crosses and the jewelled rings. Each on its base at y = 0, facing the camera (+z), about 1.4
 * tiles at most; colours from the `pu*` ramps (content/palettes/powerups.json).
 */

import { Vector3 } from 'three';
import { type MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { arc, circlePath, puffy, rotX, rotZ, slab, Smooth, tube } from './shapes';

export type Builder = (mb: MeshBuilder, p: Palette) => void;

/** A red sneaker side on, toe forward (+x): a plump upper, white sole and toe cap, laces, a yellow flash. */
export const shoe: Builder = (mb, p) => {
  const white = p.color('puWhite.2');
  const s = new Smooth();
  // The upper's side view, clockwise from the top of the heel, blown up into a rounded shoe.
  const upper: Array<[number, number]> = [
    [-0.52, 0.8], [-0.36, 0.78], [-0.18, 0.7], [0.02, 0.58], [0.24, 0.46], [0.46, 0.36], [0.62, 0.26],
    [0.68, 0.17], [0.6, 0.1], [0.0, 0.1], [-0.58, 0.1], [-0.66, 0.3], [-0.64, 0.56],
  ];
  puffy(s, upper, [-0.12, 0.38], 0.3, p.color('puRed.1'));
  s.flush(mb);
  mb.blob(0.7, 0.12, 0.3, p.color('puWhite.1'), place(0.02, 0.1, 0), { flatBottom: true, topColor: white, detail: 3 });
  mb.blob(0.26, 0.16, 0.24, white, place(0.46, 0.2, 0), { detail: 3 });
  // The padded collar round the ankle opening.
  tube(s, circlePath(0, 0, 0.17, 16), 0.06, 8, p.color('puRed.2'), { closed: true });
  s.flush(mb, rotX(-0.42, 0.8, 0, Math.PI / 2));
  mb.blob(0.14, 0.03, 0.12, p.color('ink.0'), place(-0.42, 0.82, 0), { detail: 2 });
  // Laces across the instep.
  for (let i = 0; i < 3; i++) {
    const x = -0.2 + i * 0.19;
    mb.box(0.08, 0.07, 0.4, white, rotZ(x, 0.73 - i * 0.12, -0.55), true);
  }
  const flash = p.color('puYellow.1');
  for (const z of [0.29, -0.29]) mb.blob(0.32, 0.075, 0.05, flash, rotZ(-0.06, 0.38, 0.32, z), { detail: 2 });
};

/** A magic staff: a crook-handled cane in `ramp` striped in `stripe`, gold band and knob, a ferrule in `trim`. */
export const staff = (ramp: string, trim: string, stripe = `${ramp}.2`): Builder => (mb, p) => {
  const s = new Smooth();
  const path: Vector3[] = [];
  for (let i = 0; i <= 6; i++) path.push(new Vector3(0.14 - i * 0.02, 0.08 + i * 0.14, 0));
  for (let i = 1; i <= 9; i++) path.push(arc(-0.26, 0.92, 0.28, (i / 9) * Math.PI));
  path.push(new Vector3(-0.54, 0.8, 0), new Vector3(-0.54, 0.72, 0));
  // Stripes down the shaft, the hook in one piece.
  tube(s, path, 0.125, 12, (i) => p.color(i < 6 && i % 2 === 1 ? stripe : `${ramp}.1`));
  s.flush(mb);
  mb.cylinder(0.15, 0.12, 0.12, 12, p.color(`${trim}.1`), place(0.14, 0.0, 0), p.color(`${trim}.2`));
  mb.cylinder(0.15, 0.15, 0.1, 12, p.color('puGold.1'), place(0.02, 0.8, 0), p.color('puGold.2'));
  mb.blob(0.17, 0.17, 0.17, p.color('puGold.2'), place(-0.54, 0.7, 0), { detail: 2 });
};

/** A wrapped sweet: a round middle in `ramp` with white stripes, the wrapper twisted into a fan each side. */
export const candy = (ramp: string): Builder => (mb, p) => {
  const y = 0.34;
  mb.blob(0.36, 0.31, 0.31, p.color(`${ramp}.1`), place(0, y, 0), { detail: 3 });
  const s = new Smooth();
  for (const x of [-0.13, 0.13]) {
    const ring: Vector3[] = [];
    for (let i = 0; i < 20; i++) {
      const a = (i / 20) * Math.PI * 2;
      const r = Math.sqrt(1 - (x / 0.36) ** 2);
      ring.push(new Vector3(x + Math.sin(a) * 0.06, y + Math.sin(a) * 0.31 * r, Math.cos(a) * 0.31 * r));
    }
    tube(s, ring, 0.035, 8, p.color('puWhite.2'), { closed: true });
  }
  s.flush(mb);
  for (const side of [1, -1]) {
    mb.blob(0.07, 0.09, 0.09, p.color(`${ramp}.0`), place(side * 0.36, y, 0), { detail: 2 });
    const fan = rotZ(side * 0.36, y, -side * Math.PI / 2);
    mb.cylinder(0.05, 0.26, 0.3, 8, p.color(`${ramp}.2`), fan, p.color(`${ramp}.1`));
  }
};

/** Cross outline, counter-clockwise, and its front split into five rectangles. */
function crossShape(w: number, h: number, len: number, c: number, top: number): {
  pts: Array<[number, number]>; tris: number[];
} {
  const pts: Array<[number, number]> = [
    [-w, 0], [w, 0], [w, c - h], [len, c - h], [len, c + h], [w, c + h],
    [w, top], [-w, top], [-w, c + h], [-len, c + h], [-len, c - h], [-w, c - h],
  ];
  const tris = [0, 1, 2, 0, 2, 11, 11, 2, 5, 11, 5, 8, 8, 5, 6, 8, 6, 7, 2, 3, 4, 2, 4, 5, 10, 11, 8, 10, 8, 9];
  return { pts, tris };
}

/** A bevelled cross in `ramp` with a round gold boss where the bars meet. */
export const cross = (ramp: string): Builder => (mb, p) => {
  const s = new Smooth();
  const { pts, tris } = crossShape(0.19, 0.18, 0.54, 0.86, 1.34);
  slab(s, pts, 0.28, p.color(`${ramp}.1`), {
    bevel: 0.06, bevelColor: p.color(`${ramp}.2`), sideColor: p.color(`${ramp}.0`), tris,
  });
  s.flush(mb);
  mb.cylinder(0.14, 0.11, 0.06, 16, p.color('puGold.1'), rotX(0, 0.86, 0.13, Math.PI / 2), p.color('puGold.2'));
  mb.blob(0.07, 0.07, 0.04, p.color('puWhite.2'), place(-0.03, 0.89, 0.2), { detail: 1 });
};

/** A gold ring seen a little from above, a big faceted gem in `ramp` in a gold cup on top. */
export const ring = (ramp: string): Builder => (mb, p) => {
  const s = new Smooth();
  tube(s, circlePath(0, 0, 0.38, 32), 0.09, 12, p.color('puGold.1'), { closed: true });
  s.flush(mb, rotX(0, 0.47, 0, -0.35));
  mb.cylinder(0.08, 0.2, 0.14, 12, p.color('puGold.1'), place(0, 0.82, -0.05), p.color('puGold.2'));
  mb.cylinder(0.03, 0.3, 0.2, 8, p.color(`${ramp}.1`), place(0, 0.9, -0.05), p.color(`${ramp}.2`));
  mb.cylinder(0.3, 0.19, 0.14, 8, p.color(`${ramp}.1`), place(0, 1.1, -0.05), p.color(`${ramp}.2`));
  mb.blob(0.05, 0.05, 0.03, p.color('puWhite.2'), place(-0.1, 1.2, 0.08), { detail: 1 });
};
